/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import {
  getListingAdapter,
  getListingsAdapter,
  getPartnerListingsAdapter,
  postListingAdapter,
  putListingAdapter,
} from '../../adapters/ListingsAdapter';
import { UserTypes } from '../user/UserHelpers';
import { ListingModel, PartnerListingModel } from './ListingModels';
import {
  loadingListingsAction,
  storeListingAction,
  storeListingsAction,
  storePartnerListingsAction,
} from './ListingsActionCreators';
import { ListingsActionTypes } from './ListingsActionTypes';

export function* getListingSaga(action: any): SagaIterator {
  yield put(loadingListingsAction(true));
  const listing: ListingModel = yield call(getListingAdapter, action.payload);

  yield put(storeListingAction(listing));
}

export function* postListingSaga(action: any): SagaIterator {
  const formData = new FormData();
  formData.append('userId', action.payload.userId);
  formData.append('venueName', action.payload.venueName);
  formData.append('venueAddress', action.payload.venueAddress);
  formData.append('city', action.payload.city);
  formData.append('seatingCapacity', action.payload.seatingCapacity);
  formData.append('standingCapacity', action.payload.standingCapacity);
  formData.append('totalCapacity', action.payload.totalCapacity);
  formData.append('insideFloorSpace', action.payload.insideFloorSpace);
  formData.append('outsideFloorSpace', action.payload.outsideFloorSpace);
  formData.append('totalFloorSpace', action.payload.totalFloorSpace);
  formData.append('description', action.payload.description);
  formData.append('workingHours', action.payload.workingHours);
  formData.append('objectTypes', action.payload.objectTypes);
  formData.append('eventTypes', action.payload.eventTypes);
  formData.append('objectCharacteristics', action.payload.objectCharacteristics);
  formData.append('offerFeatures', action.payload.offerFeatures);
  formData.append('maxPeople', action.payload.maxPeople);
  formData.append('minPeople', action.payload.minPeople);
  formData.append('hourlyPrice', action.payload.hourlyPrice);
  formData.append('dailyPrice', action.payload.dailyPrice);

  action.payload.photos.forEach((photo: File, index: number) => {
    const extension: string = photo.name.split('.').pop()!;
    formData.append('photos', photo, `wasp-image-${uuidv4()}.${extension}`);
  });

  formData.append('location', action.payload.location);

  const data = yield call(postListingAdapter, formData);

  yield put(loadingListingsAction(false));

  const userType = localStorage.getItem('userType');
  const isOwner = userType === UserTypes.OWNER;

  if (data.status === 'ok' && isOwner) {
    window.location.href = '/partner-profile/my-properties';
  }
}

export function* putListingSaga(action: any): SagaIterator {
  const { listingId, listing } = action.payload;

  const formData = new FormData();
  formData.append('venueName', listing.venueName);
  formData.append('userId', listing.userId);
  formData.append('venueAddress', listing.venueAddress);
  formData.append('city', listing.city);
  formData.append('seatingCapacity', listing.seatingCapacity);
  formData.append('standingCapacity', listing.standingCapacity);
  formData.append('totalCapacity', listing.totalCapacity);
  formData.append('insideFloorSpace', listing.insideFloorSpace);
  formData.append('outsideFloorSpace', listing.outsideFloorSpace);
  formData.append('totalFloorSpace', listing.totalFloorSpace);
  formData.append('description', listing.description);
  formData.append('workingHours', listing.workingHours);
  formData.append('objectTypes', listing.objectTypes);
  formData.append('eventTypes', listing.eventTypes);
  formData.append('objectCharacteristics', listing.objectCharacteristics);
  formData.append('offerFeatures', listing.offerFeatures);
  formData.append('maxPeople', listing.maxPeople);
  formData.append('minPeople', listing.minPeople);
  formData.append('hourlyPrice', listing.hourlyPrice);
  formData.append('dailyPrice', listing.dailyPrice);
  formData.append('listingId', listingId);

  listing.photos.forEach((photo: File, index: number) => {
    const extension: string = photo.name.split('.').pop()!;
    formData.append('photos', photo, `wasp-image-${uuidv4()}.${extension}`);
  });

  formData.append('location', listing.location);

  const data = yield call(putListingAdapter, formData, listingId);

  yield put(loadingListingsAction(false));

  const userType = localStorage.getItem('userType');
  const isOwner = userType === UserTypes.OWNER;

  if (data.status === 'ok' && isOwner) {
    window.location.href = '/partner-profile/my-properties';
  }
}

export function* getListingsSaga(action: any): SagaIterator {
  yield put(loadingListingsAction(true));
  const listings: ListingModel[] = yield call(
    getListingsAdapter,
    action.payload.lat,
    action.payload.lng,
    action.payload.type,
    action.payload.people,
    action.payload.radius,
    action.payload.objectTypes,
    action.payload.objectCharacteristics,
    action.payload.offerFeatures,
    action.payload.leaseOnly,
  );

  yield put(storeListingsAction(listings));
}

export function* getPartnerListingsSaga(action: any): SagaIterator {
  yield put(loadingListingsAction(true));
  const listings: PartnerListingModel[] = yield call(getPartnerListingsAdapter, action.payload);

  yield put(storePartnerListingsAction(listings));
}

export function* watchListingsAsyncActions(): SagaIterator {
  yield all([
    takeLatest(ListingsActionTypes.POST_LISTING, postListingSaga),
    takeLatest(ListingsActionTypes.PUT_LISTING, putListingSaga),
    takeEvery(ListingsActionTypes.GET_LISTINGS, getListingsSaga),
    takeEvery(ListingsActionTypes.GET_LISTING, getListingSaga),
    takeEvery(ListingsActionTypes.GET_PARTNER_LISTING, getPartnerListingsSaga),
  ]);
}
